import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import 'react-quill/dist/quill.snow.css';
import QnA from './QnA';
import moment from 'moment';
import { useAuth } from '../../context/AuthContext';
import { Controller, useForm } from 'react-hook-form';
import ReactQuill, { Quill } from 'react-quill';

import icon from '../../assets/community.svg';
import back from '../../assets/Back_Icon.svg';
import icon1 from '../../assets/icon_edit.svg';
import Delete from '../../assets/icon_delete.svg';
import fileIcon from '../../assets/fileIcon.svg';
import arr_right from '../../assets/arr_right.svg';

interface QnA {
  _id: string;
  user: {
    _id: string;
    fullName: string;
    email: string;
  };
  category: string;
  title: string;
  content: string;
  answer: {
    _id: string;
    user: string;
    content: string;
    createdAt: string;
    updatedAt: string;
  };
  createdAt: string;
  updatedAt: string;
  answerUser: {
    _id: string;
    fullName: string;
    email: string;
  };
  fileList: {
    _id: string;
    url: string;
    originalName: string;
    savedName: string;
  }[];
}

interface QnAAnswerFormData {
  answer: string;
}

const QnADetail: React.FC = () => {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [qna, setQnA] = useState<QnA | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isAnswerEdit, setIsAnswerEdit] = useState(false);
  const [isAnswerValid, setIsAnswerValid] = useState(false);
  const quillRef = useRef<ReactQuill | null>(null);
  const {
    control,
    formState: { errors },
    setValue,
    reset,
  } = useForm<QnAAnswerFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const getCategoryKorean = (category: string) => {
    const categoryMap: { [key: string]: string } = {
      general: '일반 문의',
      technical: '기술 문의',
      bug: '버그 신고',
      feature: '기능 요청',
      account: '계정 문의',
    };
    return categoryMap[category] || category;
  };

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    // 4) 클립보드 매처로 IMG 무시 (이중 안전장치)
    const Delta = Quill.import('delta');
    editor.clipboard.addMatcher('IMG', () => new Delta());

    // 2) Ctrl+V로 이미지 들어오는 것 차단
    const root = editor.root as HTMLElement;
    const onPaste = (e: ClipboardEvent) => {
      const hasImage =
        !!e.clipboardData &&
        Array.from(e.clipboardData.items).some(
          item => item.kind === 'file' && item.type.startsWith('image/')
        );
      if (hasImage) {
        e.preventDefault();
      }
    };

    // 3) 드래그&드롭 이미지 차단
    const onDrop = (e: DragEvent) => {
      const hasImage =
        !!e.dataTransfer && Array.from(e.dataTransfer.files).some(f => f.type.startsWith('image/'));
      if (hasImage) {
        e.preventDefault();
      }
    };

    root.addEventListener('paste', onPaste);
    root.addEventListener('drop', onDrop);

    return () => {
      root.removeEventListener('paste', onPaste);
      root.removeEventListener('drop', onDrop);
    };
  }, []);

  // ReactQuill 내용 검증 함수
  const validateQuillContent = (value: string) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = value;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    return textContent.trim().length > 0 || '문의 내용을 입력해주세요';
  };

  // qna detail api call
  const fetchQnADetail = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`/api/qna/${id}`, {
        params: {
          userRole: user?.role,
        },
      });
      setQnA(response.data);
      setValue('answer', response.data.answer?.content || '');
    } catch (err) {
      setError('문의 내용을 불러오는데 실패했습니다.');
      console.error('Error fetching QnA detail:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchQnADetail();
    }
  }, [id]);

  // loading
  if (isLoading) {
    return (
      <div className="qna-detail">
        <div className="qna-detail__loading">
          <p>로딩 중...</p>
        </div>
      </div>
    );
  }

  if (error || !qna) {
    return (
      <div className="qna-detail">
        <div className="qna-detail__error">
          <p>{error || '문의 내용을 찾을 수 없습니다.'}</p>
          <button onClick={() => navigate('/setting/qna-list')} className="qna-detail__back-btn">
            목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  // 문의사항 수정 완료 후 호출
  const handleEditSuccess = async () => {
    setIsEdit(false);
    await fetchQnADetail();
  };

  // qna delete button
  const handleDelete = async () => {
    if (window.confirm('해당 문의를 삭제하시겠습니까?')) {
      try {
        await axios.delete(`/api/qna/${id}`);
        alert('삭제되었습니다.');
        navigate(`/setting/qna-list?deleted=${id}`);
      } catch (error) {
        console.error('삭제 중 오류가 발생했습니다:', error);
        alert('삭제 중 오류가 발생했습니다.');
      }
    }
  };

  // 답변 수정 완료 후 호출
  const handleAnswerSave = async () => {
    setIsAnswerEdit(false);
    try {
      // answer 값
      const editor = quillRef.current?.getEditor();
      if (!editor) return;
      const content = editor.root.innerHTML;

      if (qna.answer) {
        await axios.put(`/api/qna/answer/${qna.answer._id}`, { content });
      } else {
        await axios.post(`/api/qna/answer/create/${id}`, { content });
      }
      reset();
      setIsAnswerEdit(false);
    } catch (err) {
      console.error('Error updating QnA answer:', err);
    } finally {
      await fetchQnADetail();
    }
  };

  // 첨부파일 다운로드
  const handleFileDownload = async (savedName: string, originalName: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/upload-files/download/${savedName}`, {
        params: {
          usedFor: 'qna',
        },
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('파일 다운로드 실패:', err);
    }
  };

  return (
    <div className="qna-container">
      {!isEdit && (
        <div className="page-header">
          <img src={icon} alt="Mind Thermometer Icon" className="page-header__icon" />
          <h2 className="page-header__title">문의 내역</h2>
          <p>이전 문의사항을 확인하세요.</p>
        </div>
      )}

      {isEdit ? (
        <>
          <QnA mode="edit" onEditSuccess={handleEditSuccess} />
        </>
      ) : (
        <>
          <div className="qna-detail">
            <div className="qna-detail__header">
              <button
                onClick={() => navigate('/setting/qna-list')}
                className="qna-detail__back-btn"
              >
                <img src={back} alt="목록으로" />
                <p>목록으로</p>
              </button>
              <div className="qna-detail__btn-group">
                {!qna?.answer && !isEdit && (
                  <button onClick={() => setIsEdit(true)} className="qna-detail__edit-btn">
                    <img src={icon1} alt="수정" />
                    <p>수정</p>
                  </button>
                )}
                {!isEdit && (
                  <button onClick={handleDelete} className="qna-detail__delete-btn">
                    <img src={Delete} alt="삭제" />
                    <p>삭제</p>
                  </button>
                )}
              </div>
            </div>
            <div className="qna-detail__content">
              <div className="qna-detail__question">
                <span className="qna-detail__category">{getCategoryKorean(qna.category)}</span>
                <h2 className="qna-detail__title">{qna.title}</h2>
                <div className="qna-detail__date">
                  <span>작성일 {moment(qna.updatedAt).format('YYYY-MM-DD HH:mm')}</span>
                  <span>|</span>
                  <span className="qna-detail__user">{qna.user.fullName}</span>
                </div>
                <div className="qna-detail__question-content">
                  <div
                    className="qna-detail__text"
                    dangerouslySetInnerHTML={{ __html: qna.content }}
                  />
                  {qna.fileList && qna.fileList.length > 0 && (
                    <>
                      <div className="qna-detail__attachments">
                        {qna.fileList.map((file: any) => (
                          <button
                            key={file._id}
                            rel="noopener noreferrer"
                            className="qna-detail__file-btn"
                            onClick={() => handleFileDownload(file.savedName, file.originalName)}
                          >
                            <div>
                              <img src={fileIcon} alt="업로드 파일" />
                              <p>{file.originalName}</p>
                            </div>
                            <span className="qna-detail__file-btn-icon">↓</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="qna-detail__answer">
            <div className="qna-detail__answer-header">
              <div>
                <h3>
                  <img src={arr_right} alt="" />
                  답변
                </h3>
                {user?.role === 'admin' && (
                  <div className="qna-detail__answer-btn-group">
                    {!isAnswerEdit && (
                      <>
                        <button
                          onClick={() => {
                            setIsAnswerEdit(true);
                            setValue('answer', qna.answer?.content || '');
                          }}
                          className="answer-edit-btn"
                        >
                          <img src={icon1} alt="수정" />
                          <p> {!isAnswerEdit && qna.answer ? '수정' : '작성'}</p>
                        </button>
                      </>
                    )}
                    {isAnswerEdit && (
                      <>
                        <button
                          onClick={() => {
                            setIsAnswerEdit(false);
                            reset();
                            setIsAnswerValid(false);
                          }}
                          className="answer-cancel-btn"
                        >
                          취소
                        </button>
                        <button
                          onClick={handleAnswerSave}
                          className="answer-save-btn"
                          disabled={!isAnswerValid}
                        >
                          저장
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
              {qna.answerUser && (
                <div className="qna-detail__date">
                  <span>답변일 {moment(qna.updatedAt).format('YYYY-MM-DD HH:mm')}</span>
                  <span>|</span>
                  <span className="qna-detail__user">{qna.answerUser?.fullName}</span>
                </div>
              )}
            </div>
            {qna.answer && !isAnswerEdit ? (
              <div className="qna-detail__answer-content">
                <div
                  className="qna-detail__text"
                  dangerouslySetInnerHTML={{ __html: qna.answer.content }}
                />
              </div>
            ) : isAnswerEdit ? (
              <div className="form-group">
                <div translate="no">
                  <Controller
                    name="answer"
                    control={control}
                    rules={{ validate: validateQuillContent, required: '답변을 입력해주세요' }}
                    render={({ field }) => (
                      <ReactQuill
                        id="answer"
                        className="quill-input"
                        theme="snow"
                        value={field.value}
                        onChange={value => {
                          field.onChange(value);
                          const tempDiv = document.createElement('div');
                          tempDiv.innerHTML = value;
                          const text = tempDiv.textContent?.trim() || '';
                          setIsAnswerValid(text.length > 0);
                        }}
                        ref={quillRef}
                      />
                    )}
                  />
                </div>
                {errors.answer && <span className="error-message">{errors.answer.message}</span>}
              </div>
            ) : (
              <div className="qna-detail__no-answer">
                <p>답변 내역이 없습니다.</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default QnADetail;
